import React, { FC } from 'react';
import styled from 'styled-components';
import Stack from 'src/components/widgets/stack/stack';
import { BasicButton, GhostButton } from 'src/components/widgets/buttons';
import { T } from 'src/shared/helpers';
import { AgentAnswerRecap, AgentResultRecap, AgentTurnRequest } from './types';

const Card = styled(Stack)`
    min-width: 0;
    padding: 12px;
    border: 1px solid #dce4e8;
    border-radius: 10px;
    background: #fafcfd;
    color: #37474f;
    font-size: 12px;
`;
const Rows = styled.dl`
    display: grid;
    grid-template-columns: minmax(75px, 0.8fr) minmax(0, 1.6fr);
    gap: 8px 12px;
    margin: 0;
    dt {
        color: #607d8b;
        overflow-wrap: anywhere;
    }
    dd {
        margin: 0;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
    }
`;
const Details = styled.details`
    min-width: 0;
    summary {
        cursor: pointer;
        color: #546e7a;
        font-size: 11px;
    }
    ul {
        padding-left: 18px;
        margin: 8px 0 0;
    }
    li {
        padding: 3px 0;
        overflow-wrap: anywhere;
    }
`;
const StatusLine = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #546e7a;
    &::before {
        content: '';
        width: 7px;
        height: 7px;
        flex-shrink: 0;
        border-radius: 50%;
        background: #00899d;
    }
`;
const Activity = styled(Stack)`
    padding: 10px 4px;
`;
const Actions = styled(Stack)`
    flex-wrap: wrap;
`;

const activityLabel = (code: string): string =>
    ({
        thinking: T._('Processing your request…', 'Admin'),
        prepare_response: T._('Preparing the response…', 'Admin'),
        validate_draft: T._('Checking the draft…', 'Admin'),
        read_product: T._('Reading the product configuration…', 'Admin'),
        read_printing_method: T._('Checking printing methods…', 'Admin'),
        read_pricing: T._('Checking pricing settings…', 'Admin'),
        generate_images: T._('Generating product images…', 'Admin'),
        check_images: T._('Checking generated images…', 'Admin'),
        detect_areas: T._('Finding print areas…', 'Admin'),
        edit_areas: T._('Configuring print areas…', 'Admin'),
        set_scale: T._('Setting the image scale…', 'Admin'),
        edit_variants: T._('Configuring product variants…', 'Admin'),
        edit_sides: T._('Configuring product sides…', 'Admin'),
        edit_printing_method: T._('Preparing the printing method…', 'Admin'),
        edit_pricing: T._('Preparing pricing rules…', 'Admin'),
        validate_plan: T._('Verifying the proposed changes…', 'Admin'),
        apply_product: T._('Applying the approved product changes…', 'Admin'),
        apply_printing_method: T._('Applying the approved printing method changes…', 'Admin'),
        apply_pricing: T._('Applying the approved pricing changes…', 'Admin'),
        edit_draft: T._('Updating the draft…', 'Admin')
    })[code] ?? T._('Working on your request…', 'Admin');

const resultLabel = (code: string): string =>
    ({
        edit_areas: T._('Print areas', 'Admin'),
        detect_areas: T._('Print area detection', 'Admin'),
        set_scale: T._('Image scale', 'Admin'),
        edit_variants: T._('Product variants', 'Admin'),
        edit_sides: T._('Product sides and images', 'Admin'),
        edit_printing_method: T._('Printing method configuration', 'Admin'),
        edit_pricing: T._('Pricing configuration', 'Admin'),
        edit_draft: T._('Draft changes', 'Admin'),
        generate_images: T._('Image generation', 'Admin'),
        check_images: T._('Generated images', 'Admin'),
        apply_product: T._('Product changes', 'Admin'),
        apply_printing_method: T._('Printing method changes', 'Admin'),
        apply_pricing: T._('Pricing changes', 'Admin'),
        validate_plan: T._('Change validation', 'Admin'),
        read_product: T._('Product lookup', 'Admin'),
        read_printing_method: T._('Printing method lookup', 'Admin'),
        read_pricing: T._('Pricing lookup', 'Admin')
    })[code] ?? T._('Operation', 'Admin');

export const AgentActivityIndicator: FC<{ request: AgentTurnRequest | null; restoring: boolean; refreshing: boolean }> = ({
    request,
    restoring,
    refreshing
}) => {
    const activities = request?.progress?.activities ?? [];
    const current = [...activities].reverse().find((activity) => activity.state === 'running');
    const label = restoring
        ? T._('Restoring your conversation…', 'Admin')
        : refreshing
          ? T._('Updating the Preview…', 'Admin')
          : request?.state === 'queued'
            ? T._('Waiting to start your request…', 'Admin')
            : current
              ? activityLabel(current.code)
              : T._('Processing your request…', 'Admin');
    // Collapse adjacent repetitions only; never invent a planned or completed step.
    const visible = activities.filter(
        (activity, index) => index === activities.length - 1 || activities[index + 1].code !== activity.code || activities[index + 1].state !== activity.state
    );
    return (
        <Activity $gap='7px'>
            <StatusLine role='status' aria-live='polite' aria-atomic='true'>
                {label}
            </StatusLine>
            {visible.length > 0 && (
                <Details>
                    <summary>{T._('Show activity', 'Admin')}</summary>
                    <ul>
                        {visible.map((activity) => (
                            <li key={activity.id}>
                                {activity.state === 'completed' ? '✓ ' : activity.state === 'failed' ? '• ' : '… '}
                                {activityLabel(activity.code)} {activity.state === 'failed' && `(${T._('Not completed', 'Admin')})`}
                            </li>
                        ))}
                    </ul>
                </Details>
            )}
        </Activity>
    );
};

export const AgentAnswerRecapCard: FC<{ recap: AgentAnswerRecap; onEdit?: () => void; disabled?: boolean }> = ({ recap, onEdit, disabled }) => (
    <Card $gap='10px'>
        <strong>{recap.correctionAfterTurnId ? T._('Updated answers sent', 'Admin') : T._('Answers sent', 'Admin')}</strong>
        <Rows>
            {recap.answers.map((answer) => (
                <React.Fragment key={answer.questionId}>
                    <dt>{answer.title}</dt>
                    <dd>{answer.skipped ? T._('Skipped', 'Admin') : answer.text || answer.labels.join(', ')}</dd>
                </React.Fragment>
            ))}
        </Rows>
        <Details>
            <summary>{T._('Show questions', 'Admin')}</summary>
            <ul>
                {recap.answers.map((answer) => (
                    <li key={answer.questionId}>{answer.question}</li>
                ))}
            </ul>
        </Details>
        {onEdit && (
            <Actions $horizontal $gap='8px'>
                <GhostButton type='button' $size='small' disabled={disabled} onClick={onEdit}>
                    {T._('Edit answers', 'Admin')}
                </GhostButton>
            </Actions>
        )}
    </Card>
);

export const AgentResultRecapCard: FC<{ recap: AgentResultRecap; onPreview?: () => void }> = ({ recap, onPreview }) => {
    const title =
        recap.state === 'partial'
            ? T._('Some steps need attention', 'Admin')
            : recap.state === 'approval_required'
              ? T._('Changes prepared for approval', 'Admin')
              : recap.state === 'applied'
                ? T._('Changes applied', 'Admin')
                : T._('Draft updated', 'Admin');
    const stateLabel = (state: string) =>
        state === 'applied' ? T._('Applied', 'Admin') : state === 'failed' ? T._('Not completed', 'Admin') : T._('Saved in draft', 'Admin');
    return (
        <Card $gap='10px'>
            <strong>{title}</strong>
            <Rows>
                {recap.items.slice(0, 3).map((item, index) => (
                    <React.Fragment key={index}>
                        <dt>{resultLabel(item.code)}</dt>
                        <dd>{stateLabel(item.state)}</dd>
                    </React.Fragment>
                ))}
            </Rows>
            <Details>
                <summary>{T._('Show details', 'Admin')}</summary>
                <ul>
                    {recap.items.map((item, index) => (
                        <li key={index}>
                            {resultLabel(item.code)} · {stateLabel(item.state)} · {item.count} {T._('operations', 'Admin')}
                        </li>
                    ))}
                </ul>
            </Details>
            {onPreview && (
                <Actions $horizontal $gap='8px'>
                    <BasicButton type='button' $size='small' onClick={onPreview}>
                        {T._('Open Preview', 'Admin')}
                    </BasicButton>
                </Actions>
            )}
        </Card>
    );
};
